package macha.sh.kotlin.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import macha.sh.kotlin.SiteColors
import macha.sh.kotlin.SiteModifiers
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import macha.sh.kotlin.components.layouts.PageLayoutData
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.window
import kotlinx.coroutines.await

@InitRoute
fun initBlogGuidesPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Blog & Guides",
            description = "Technical articles, guides, and opinions by Macha.",
            path = "/blog-guides",
            indexable = false,
        )
    )
}
@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun BlogGuidesPage() { GuideStructure.Content() }

object GuideStructure {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content() = Box(SiteModifiers.PrimaryPanel, contentAlignment = Alignment.Center) {
        Column(modifier = Mods.mainColumn(), horizontalAlignment = Alignment.CenterHorizontally) {
            H1 { Text("Blog & Guides") }
            AnnouncementFeed.Content()
            Box(modifier = Modifier.fillMaxWidth()) {
                Row(modifier = Mods.row()) { 
                    SubMenu.Content()
                    ArticleListContainer.Content()
                }
            }
        }
    }
}

object AnnouncementFeed {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content() = Box(modifier = Mods.feedPanel()) {
        SpanText("Announcements and Bluesky Feed will Go here!", modifier = Modifier.applyFont(SiteTypography.Body))
    }
}

object SubMenu {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content() = Box(modifier = Mods.subMenu(), contentAlignment = Alignment.CenterEnd) {
        Column(modifier = Mods.subMenuColumn()) {
            val categories = BlogCategories.remember()
            categories?.forEach { cat ->
                SubMenuItem.Content(prefix = "./", mainText = cat)
            }
        }
    }
}

// Loader for blog categories stored at
// resources/public/text-content/blog-posts/categories.json
object BlogCategories {
    suspend fun load(url: String = "text-content/blog-posts/categories.json"): List<String> {
        val resp = window.fetch(url).await()
        if (!resp.ok) throw IllegalStateException("Failed to load categories (HTTP ${'$'}{resp.status})")
        val txt = resp.text().await()
        val parsed = js("JSON.parse")(txt)
        val isArray = js("Array.isArray")(parsed) as Boolean
        return when {
            isArray -> (parsed as Array<String>).toList()
            js("Array.isArray")(parsed.categories) as Boolean -> (parsed.categories as Array<String>).toList()
            else -> error("Invalid categories.json format: expected array or { categories: [] }")
        }
    }

    @Composable
    fun remember(url: String = "text-content/blog-posts/categories.json"): List<String>? {
        var cats by remember(url) { mutableStateOf<List<String>?>(null) }
        LaunchedEffect(url) {
            runCatching { load(url) }
                .onSuccess { cats = it }
                .onFailure { console.error("Failed to load blog categories:", it) }
        }
        return cats
    }
}

// Kotlin/JS exposes encodeURIComponent as a browser global; it is not
// importable as a Kotlin symbol, so wrap it.
internal fun encodeURIComponent(value: String): String = js("encodeURIComponent")(value) as String

data class ArticleMeta(
    val title: String,
    val slug: String,
    val description: String,
    val tags: List<String>,
    val date: String,
    val file: String,
)

// Loader for published articles listed at
// resources/public/text-content/blog-posts/articles.json
// (generated from Obsidian vault frontmatter by scripts/gen-articles-manifest.py)
object Articles {
    suspend fun load(url: String = "text-content/blog-posts/articles.json"): List<ArticleMeta> {
        val resp = window.fetch(url).await()
        if (!resp.ok) throw IllegalStateException("Failed to load articles (HTTP ${resp.status})")
        val txt = resp.text().await()
        val parsed = js("JSON.parse")(txt)
        if (!js("Array.isArray")(parsed.articles) as Boolean) {
            error("Invalid articles.json format: expected { articles: [] }")
        }
        return (parsed.articles as Array<dynamic>).map { a ->
            ArticleMeta(
                title = a.title as String,
                slug = a.slug as? String ?: "",
                description = a.description as? String ?: "",
                tags = (a.tags as? Array<dynamic>)?.map { it as String } ?: emptyList(),
                date = a.date as? String ?: "",
                file = a.file as String,
            )
        }
    }

    @Composable
    fun remember(url: String = "text-content/blog-posts/articles.json"): List<ArticleMeta>? {
        var articles by remember(url) { mutableStateOf<List<ArticleMeta>?>(null) }
        LaunchedEffect(url) {
            runCatching { load(url) }
                .onSuccess { articles = it }
                .onFailure { console.error("Failed to load blog articles:", it) }
        }
        return articles
    }
}

object SubMenuItem {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content(prefix: String, mainText: String) = Box(modifier = Mods.subMenuItem()) {
        Span(Modifier.toAttrs()) {
            Span(Mods.subMenuPrefix().toAttrs()) { Text(prefix) }
            Span(Mods.subMenuText().toAttrs()) { Text(mainText) }
        }
    }
}

object ArticleListContainer {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content() = Box(modifier = Mods.contentArea()) {
        Column(modifier = Modifier.fillMaxWidth()) {
            val articles = Articles.remember()
            when {
                articles == null -> {} // still loading
                articles.isEmpty() -> SpanText("No articles published yet.", modifier = Mods.descText())
                else -> articles.forEach { ArticleBox.Content(it) }
            }
        }
    }
}

object ArticleBox {
    private val Mods = SiteModifiers.Blog
    @Composable
    fun Content(meta: ArticleMeta) = Box(
        modifier = Mods.articleContainer()
            .cursor(Cursor.Pointer)
            .onClick { window.location.href = "/blogguides/post?post=" + encodeURIComponent(meta.slug) }
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Mods.articleInfoBox()) {
                    Box(modifier = Mods.articleContainer()) {
                        SpanText(meta.title, modifier = Mods.titleText())
                    }
                    Box(modifier = Mods.articleContainer()) {
                        SpanText(meta.description, modifier = Mods.descText())
                    }
                }
                Box(modifier = Mods.articleImgBox()) {
                    SpanText(meta.tags.firstOrNull()?.uppercase() ?: "MD", modifier = Modifier.applyFont(SiteTypography.Subheading))
                }
            }
        }
    }
}
